import { useContext, useState } from "react";
import PropTypes from "prop-types";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  ButtonGroup,
  LinkButton,
  Text,
  Link,
  IllustratedMessage,
  Heading,
  DialogTrigger,
  Disclosure,
  DisclosureTitle,
  DisclosurePanel,
  CenterBaseline,
} from "@react-spectrum/s2";
import { Button as AriaButton } from "react-aria-components/Button";
import DownloadIcon from "@react-spectrum/s2/icons/Download";
import ShareIcon from "@react-spectrum/s2/icons/Share";
import OpenInIcon from "@react-spectrum/s2/icons/OpenIn";
import CheckmarkCircleIcon from "@react-spectrum/s2/icons/CheckmarkCircle";
import { VideoWizardContext } from "@app/views/projects/videoWizardContext";
import { ShareVideoDialog } from "@app/views/projects/ShareVideoDialog";
import { useVideoAssets, useUpdateVideo } from "@app/hooks/videos";
import { presignURLQueryOptions } from "@app/hooks/s3";
import { store } from "@app/store";
import { Step } from "./Step";
import { S3Image } from "@app/components/S3Image";
import { UserFeedback } from "@app/components/UserFeedback";

import { style, size } from "@react-spectrum/s2/style" with { type: "macro" };

const tabButtonStyle = style({
  display: "inline-flex",
  alignItems: "baseline",
  gap: size(6),
  paddingX: { default: size(8), md: size(16) },
  paddingY: 8,
  borderRadius: { default: "lg", md: "pill" },
  borderWidth: 0,
  backgroundColor: {
    default: "transparent",
    isHovered: "transparent-overlay-50",
    variant: {
      active: "transparent-overlay-100",
    },
  },
  transition: "colors",
  textAlign: "start",
});

const TabButton = ({ variant, onPress, children }) => (
  <AriaButton
    variant={variant === "active" ? "active" : "quiet"}
    onPress={onPress}
    className={(renderProps) =>
      tabButtonStyle({ ...renderProps, variant, staticColor: "auto" })
    }
  >
    {children}
  </AriaButton>
);

const FigmaDeckDuplicate = ({
  video,
  assets,
  figmaTemplatePath,
  variant = "accent",
}) => {
  const figmaTemplateUrl = figmaTemplatePath
    ? `https://www.figma.com/${figmaTemplatePath}/duplicate`
    : undefined;

  const [figmaLicense, setFigmaLicense] = useState("collab");

  const s3AssetsQueries = useQueries({
    queries: assets.map((asset) => presignURLQueryOptions(asset?.filePath)),
  });

  const brandColor = video.data?.customer?.["Brand-color"];

  const secondaryColor = video.data?.customer?.["Secondary-color"];

  return (
    <div
      className={style({
        alignSelf: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 32,
        width: "full",
        boxSizing: "border-box",
        maxWidth: 900,
        padding: { default: 24, md: 32 },
        backgroundColor: "layer-2",
        boxShadow: "emphasized",
        borderRadius: "xl",
        marginX: "auto",
      })}
    >
      <div>
        <Heading
          styles={style({
            font: "ui-lg",
            marginBottom: 12,
            textAlign: "center",
          })}
        >
          You need a Figma Collab license to proceed
        </Heading>

        <div
          className={style({
            display: "flex",
            flexDirection: {
              default: "column",
              md: "row",
            },
            flexShrink: 1,
            minWidth: 0,
            gap: 4,
            // padding: 24,
            padding: 8,
            borderRadius: { default: "xl", md: "pill" },
            backgroundColor: "layer-1",
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "transparent-overlay-100",
            // boxShadow: "emphasized",
            marginBottom: 16,
          })}
        >
          <TabButton
            variant={figmaLicense === "collab" ? "active" : "quiet"}
            onPress={() => setFigmaLicense("collab")}
          >
            <CenterBaseline styles={style({ display: "inline-flex" })}>
              <CheckmarkCircleIcon styles={style({ size: 18 })} />
            </CenterBaseline>
            <Text styles={style({ font: { default: "ui-sm", md: "ui" } })}>
              I have a Collab or Full Figma license
            </Text>
          </TabButton>
          <TabButton
            variant={figmaLicense === "restricted" ? "active" : "quiet"}
            onPress={() => setFigmaLicense("restricted")}
          >
            <Text styles={style({ font: { default: "ui-sm", md: "ui" } })}>
              I only have Viewer license (or none)
            </Text>
          </TabButton>
        </div>
      </div>

      {figmaLicense === "collab" && (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 603 211"
            className={style({ width: "full", maxWidth: 800 })}
          >
            <rect
              width="222.226"
              height="135.195"
              x="359.83"
              y="75.579"
              fill="#000"
              fillOpacity=".25"
              rx="5.273"
            />
            <mask id="a" fill="#fff">
              <path d="M572.021 87.625c1.126.594 1.126 2.206 0 2.799l-4.121 2.173a1.582 1.582 0 0 1-2.32-1.4v-4.345a1.582 1.582 0 0 1 2.32-1.4z" />
            </mask>

            <path
              stroke="#fff"
              strokeOpacity=".5"
              strokeWidth="1.055"
              d="M565.58 86.852v4.345a1.582 1.582 0 0 0 2.32 1.4l4.121-2.173c1.126-.593 1.126-2.205 0-2.799l-4.121-2.172a1.582 1.582 0 0 0-2.32 1.399Z"
              mask="url(#a)"
            />
            <rect
              width="39.714"
              height="28.729"
              x="314.201"
              y="75.579"
              fill="#000"
              fillOpacity=".25"
              rx="2.636"
            />
            <rect
              width="39.714"
              height="28.729"
              x="314.201"
              y="111.068"
              fill="#000"
              fillOpacity=".25"
              rx="2.636"
            />
            <rect
              width="39.714"
              height="28.729"
              x="314.201"
              y="146.557"
              fill="#000"
              fillOpacity=".25"
              rx="2.636"
            />
            <rect
              width="39.714"
              height="28.729"
              x="314.201"
              y="182.045"
              fill="#000"
              fillOpacity=".25"
              rx="2.636"
            />
            <path
              fill="#fff"
              d="M418.236 137.578h3.246c.133 0 .171.019.209.133.911 2.847 1.31 4.878 1.557 5.676a270 270 0 0 1 1.613-5.657c.038-.114.095-.152.209-.152h2.79c.095 0 .152.038.114.171-1.253 3.967-3.208 9.225-4.688 12.661-.038.114-.076.171-.304.19l-2.98.209c-.133 0-.19-.095-.133-.209a262 262 0 0 0 1.727-3.626c.057-.133.076-.209.019-.36-.626-1.481-2.581-6.378-3.511-8.903-.038-.095 0-.133.132-.133m-7.26 0h2.6c.152 0 .209.057.266.209.114.228.209.778.247 1.215.683-.949 1.86-1.557 3.056-1.614.152 0 .19.057.19.171v2.999c0 .114-.038.171-.209.171-1.063-.038-2.297.285-2.885.892v5.296c0 .133-.057.171-.19.171h-2.904c-.152 0-.228-.038-.228-.19v-6.472c0-.855-.019-1.975-.095-2.715 0-.095.038-.133.152-.133m-6.512-.19c3.284 0 4.859 2.392 4.859 4.879 0 3.151-2.031 5.011-4.935 5.011-3.455 0-4.955-2.392-4.955-4.935 0-2.848 1.975-4.955 5.031-4.955m-.095 2.544c-.93 0-1.576.835-1.576 2.373 0 1.518.646 2.429 1.633 2.429.949 0 1.575-.816 1.575-2.391 0-1.443-.531-2.411-1.632-2.411m-5.915 4.878v1.88c0 .113-.038.208-.152.265-.588.247-1.176.323-1.936.323-1.841 0-2.885-.892-2.885-2.866v-4.271h-1.101c-.114-.019-.152-.076-.152-.209v-2.183c0-.133.038-.171.19-.171h1.082c.038-.626.095-1.632.228-2.221.019-.114.076-.17.19-.189l2.809-.342c.095-.019.171-.019.171.133-.038.569-.076 1.67-.095 2.619h1.556c.133 0 .171.038.171.152v2.24c0 .133-.038.152-.152.171h-1.594v3.492c0 .817.209 1.101.949 1.101.19 0 .285-.019.569-.076.076-.019.152.019.152.152m-11.946 2.468c-1.442 0-2.6-.228-3.473-.645-.133-.057-.171-.152-.171-.342v-2.696c0-.113.076-.189.189-.113a7.2 7.2 0 0 0 3.702.987c1.196 0 1.613-.323 1.613-.779 0-.455-.322-.702-1.594-1.214l-.854-.323c-2.145-.873-3.056-2.012-3.056-3.702 0-1.955 1.423-3.872 4.897-3.872 1.177 0 2.316.171 3.056.475.114.038.133.133.133.265v2.601c0 .076-.057.171-.19.095-.835-.456-2.012-.684-3.132-.684-1.177 0-1.556.399-1.556.817 0 .379.17.664 1.613 1.196l.759.284c2.677.987 3.265 2.221 3.265 3.797 0 2.543-2.183 3.853-5.201 3.853m48.195-22.081h-3.474c.095.74.797 1.442 2.43 1.442.911 0 1.613-.151 2.334-.398.057-.019.133-.019.133.114v2.069c0 .19-.057.228-.152.284-.816.361-1.727.532-3.056.532-3.72 0-5.049-2.43-5.049-4.802 0-2.582 1.595-5.031 4.765-5.031 3.075 0 4.27 2.202 4.27 4.271 0 .494-.037.987-.094 1.31-.019.076-.076.133-.171.152a26 26 0 0 1-1.936.057m-3.493-2.088h1.803c.418 0 .722-.019.797-.038v-.076c-.019-.645-.455-1.253-1.252-1.253-.911 0-1.329.721-1.348 1.367m-13.781-7.346h2.943c.17 0 .208.038.208.19l-.019 3.587a6.4 6.4 0 0 1 1.519-.152c2.866 0 4.594 2.05 4.594 4.575 0 3.189-2.601 5.315-5.809 5.315-1.499 0-2.543-.19-3.474-.513-.075-.019-.132-.113-.132-.208v-12.623q0-.171.17-.171m5.866 8.485c0-1.822-.987-2.297-2.012-2.297-.209 0-.494.038-.703.076v4.65c.171.038.361.057.589.057 1.177 0 2.126-.816 2.126-2.486m-12.492-4.86c3.284 0 4.86 2.392 4.86 4.879 0 3.151-2.031 5.011-4.936 5.011-3.454 0-4.954-2.392-4.954-4.935 0-2.848 1.974-4.955 5.03-4.955m-.095 2.544c-.93 0-1.575.835-1.575 2.373 0 1.518.645 2.429 1.632 2.429.949 0 1.576-.816 1.576-2.391 0-1.443-.532-2.411-1.633-2.411m-10.877 7.346c-2.923 0-5.087-1.689-5.087-4.878 0-2.658 1.879-5.012 5.58-5.012.266 0 .399 0 .532.019v-3.492c0-.095.038-.152.133-.152h3.056c.095 0 .114.038.114.133v10.611c0 .55.019 1.271.057 1.689.019.133 0 .171-.114.228a11.7 11.7 0 0 1-4.271.854m1.025-2.62v-4.669c-.114-.019-.304-.076-.589-.076-1.063 0-2.126.702-2.126 2.354 0 1.632.836 2.467 2.069 2.467.285 0 .475-.038.646-.076m-10.893-.038h-3.91l-.722 2.278c-.038.152-.114.19-.246.19h-2.772c-.171 0-.209-.076-.171-.228l3.607-10.554c.076-.208.152-.512.209-1.366 0-.114.038-.171.133-.171h3.758c.095 0 .133.019.171.114l4.214 11.996c.038.152 0 .209-.133.209h-3.113c-.114 0-.171-.038-.228-.152zm-3.189-2.809h2.43c-.361-1.272-.836-2.885-1.215-4.309h-.019c-.304 1.348-.835 3.132-1.196 4.309m27.204-17.614h-3.473c.095.74.797 1.442 2.429 1.442.911 0 1.614-.151 2.335-.398.057-.019.133-.019.133.114v2.069c0 .19-.057.228-.152.284-.816.361-1.727.532-3.056.532-3.72 0-5.049-2.43-5.049-4.802 0-2.582 1.594-5.031 4.764-5.031 3.075 0 4.271 2.202 4.271 4.271 0 .494-.038.987-.095 1.31-.019.076-.076.133-.171.152-.227.019-.892.057-1.936.057m-3.492-2.088h1.803c.418 0 .721-.019.797-.038v-.076c-.019-.645-.455-1.253-1.253-1.253-.911 0-1.328.721-1.347 1.367m-13.896 5.77v-7.08c0-.626-.038-1.556-.076-2.05-.019-.114.019-.171.133-.171h2.524c.19 0 .247.038.304.171.095.171.133.323.171.513a4.13 4.13 0 0 1 2.6-.874c1.538 0 3.303.874 3.303 3.911v5.599c0 .133-.057.19-.189.19h-2.905c-.133 0-.209-.057-.209-.19v-5.523c0-.836-.417-1.31-1.176-1.31-.437 0-.798.152-1.177.436v6.416q0 .171-.171.171h-2.942c-.114 0-.19-.038-.19-.209m-7.652-12.299c3.891 0 6.036 2.998 6.036 6.263 0 3.834-2.335 6.435-6.093 6.435s-6.036-2.601-6.036-6.359c0-3.493 2.315-6.34 6.093-6.34m-2.677 6.339c0 2.069.968 3.569 2.715 3.569 1.632 0 2.581-1.253 2.581-3.55 0-2.145-.911-3.55-2.676-3.55-1.689 0-2.62 1.424-2.62 3.531"
              opacity=".5"
            />
            <foreignObject x="383" y="181" width="185.6" height="22.7">
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                className="customer-name"
                style={{
                  fontSize: "12.13px",
                  lineHeight: "18.45px",
                  fontWeight: 900,
                  fontFamily: "Adobe Clean",
                  color: "#fff",
                  textAlign: "left",
                  display: "inline-block",
                  opacity: 0.5,
                }}
              >
                {video.data?.customer?.name}
              </div>
            </foreignObject>
            <rect
              width="83.648"
              height="56.952"
              x="51.633"
              y="49.833"
              fill="url(#b)"
              opacity=".25"
              rx="4.746"
            />
            <rect
              width="43.307"
              height="29.662"
              y="34.776"
              fill="url(#c)"
              opacity=".1"
              rx="4.746"
            />
            <g filter="url(#d)">
              <rect
                width="177.974"
                height="121.615"
                x="126.668"
                y="25.509"
                fill="url(#e)"
                rx="4.746"
                shapeRendering="crispEdges"
              />
            </g>
            <g filter="url(#f)">
              <rect
                width="61.104"
                height="59.325"
                x="263.113"
                y="108.564"
                fill="url(#g)"
                rx="4.746"
                shapeRendering="crispEdges"
              />
            </g>
            <g filter="url(#h)">
              <rect
                width="27.883"
                height="27.883"
                x="118.363"
                y="17.797"
                fill={brandColor}
                rx="2.966"
              />
            </g>
            <g filter="url(#i)">
              <rect
                width="27.883"
                height="27.883"
                x="84.547"
                y="31.442"
                fill={secondaryColor}
                rx="2.966"
              />
            </g>
            <g clipPath="url(#j)">
              <path
                fill="#00B6FF"
                d="M596.539 196.598c0-1.313 1.084-2.376 2.42-2.376h.35l.03-.001c1.499 0 2.715 1.195 2.715 2.667s-1.216 2.667-2.715 2.667l-.03-.001-.35.001c-1.336 0-2.42-1.065-2.42-2.377z"
              />
              <path
                fill="#24CB71"
                d="M591.055 202.222c0-1.473 1.215-2.667 2.715-2.667h2.77v2.639c0 1.488-1.242 2.694-2.757 2.694-1.499 0-2.728-1.194-2.728-2.666"
              />
              <path
                fill="#FF7237"
                d="M596.539 188.888v5.334h2.8c1.499 0 2.715-1.195 2.715-2.667 0-1.473-1.216-2.667-2.715-2.667z"
              />
              <path
                fill="#FF3737"
                d="M591.055 191.555c0 1.473 1.215 2.667 2.715 2.667h2.77v-5.333h-2.771c-1.499 0-2.714 1.194-2.714 2.666"
              />
              <path
                fill="#874FFF"
                d="M591.055 196.888c0 1.473 1.215 2.667 2.715 2.667h2.77v-5.333h-2.771c-1.499 0-2.714 1.194-2.714 2.666"
              />
            </g>
            <defs>
              <pattern
                id="b"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
              >
                <rect
                  width="1"
                  height="1"
                  className={style({ fill: "transparent-overlay-200" })}
                />
                <use href="#k" transform="matrix(.00083 0 0 .00122 0 -.048)" />
              </pattern>
              <pattern
                id="c"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
              >
                <rect
                  width="1"
                  height="1"
                  className={style({ fill: "transparent-overlay-200" })}
                />
                <use href="#l" transform="matrix(.00083 0 0 .00122 0 -.045)" />
              </pattern>
              <pattern
                id="e"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
              >
                <rect
                  width="1"
                  height="1"
                  className={style({ fill: "transparent-overlay-200" })}
                />
                <use href="#m" transform="matrix(.00083 0 0 .00122 0 -.046)" />
              </pattern>
              <pattern
                id="g"
                width="1"
                height="1"
                patternContentUnits="objectBoundingBox"
              >
                <rect
                  width="1"
                  height="1"
                  className={style({ fill: "transparent-overlay-200" })}
                />
                <use href="#n" transform="matrix(.0025 0 0 .00258 0 -.015)" />
              </pattern>
              <filter
                id="d"
                width="230.178"
                height="173.821"
                x="100.565"
                y="1.78"
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  result="hardAlpha"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                />
                <feOffset dy="2.373" />
                <feGaussianBlur stdDeviation="13.051" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0" />
                <feBlend
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_150_2942"
                />
                <feBlend
                  in="SourceGraphic"
                  in2="effect1_dropShadow_150_2942"
                  result="shape"
                />
              </filter>
              <filter
                id="f"
                width="125.174"
                height="123.395"
                x="231.078"
                y="78.901"
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  result="hardAlpha"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                />
                <feOffset dy="2.373" />
                <feGaussianBlur stdDeviation="16.018" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.75 0" />
                <feBlend
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_150_2942"
                />
                <feBlend
                  in="SourceGraphic"
                  in2="effect1_dropShadow_150_2942"
                  result="shape"
                />
              </filter>
              <filter
                id="h"
                width="68.224"
                height="68.223"
                x="98.193"
                y="0"
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  result="hardAlpha"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                />
                <feOffset dy="2.373" />
                <feGaussianBlur stdDeviation="10.085" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.45 0" />
                <feBlend
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_150_2942"
                />
                <feBlend
                  in="SourceGraphic"
                  in2="effect1_dropShadow_150_2942"
                  result="shape"
                />
              </filter>
              <filter
                id="i"
                width="68.224"
                height="68.223"
                x="64.376"
                y="13.645"
                colorInterpolationFilters="sRGB"
                filterUnits="userSpaceOnUse"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  result="hardAlpha"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                />
                <feOffset dy="2.373" />
                <feGaussianBlur stdDeviation="10.085" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.45 0" />
                <feBlend
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_150_2942"
                />
                <feBlend
                  in="SourceGraphic"
                  in2="effect1_dropShadow_150_2942"
                  result="shape"
                />
              </filter>
              <image
                id="k"
                width="1200"
                height="896"
                data-name="Ideation-primary.png"
                href={s3AssetsQueries?.[3]?.data?.presignURL}
                preserveAspectRatio="none"
              />
              <image
                id="l"
                width="1200"
                height="896"
                data-name="KV-alternative.png"
                href={s3AssetsQueries?.[2]?.data?.presignURL}
                preserveAspectRatio="none"
              />
              <image
                id="m"
                width="1200"
                height="896"
                data-name="KV-primary.png"
                href={s3AssetsQueries?.[1]?.data?.presignURL}
                preserveAspectRatio="none"
              />
              <image
                id="n"
                width="400"
                height="400"
                data-name="App-icon.jpg"
                href={s3AssetsQueries?.[0]?.data?.presignURL}
                preserveAspectRatio="none"
              />
              <clipPath id="j">
                <path fill="#fff" d="M591.055 188.888h11v16h-11z" />
              </clipPath>
            </defs>
          </svg>

          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              textAlign: "center",
            })}
          >
            <Text styles={style({ font: "body-lg" })}>
              Use the BeStory plugin in Figma Slides to fill your One Adobe deck
              with the {video.data?.customer?.name} assets.
            </Text>
            <LinkButton
              variant={variant}
              size="XL"
              href={figmaTemplateUrl}
              target="_blank"
              isDisabled={!figmaTemplatePath}
            >
              <OpenInIcon />
              <Text>Complete my deck in Figma Slides</Text>
            </LinkButton>
            <Text styles={style({ font: "detail" })}>
              Note: In case of issues with Figma Collab license activation,
              please reach out to the Figma license management team:
              <br />
              <Link href="mailto:cesarc@adobe.com">cesarc@adobe.com</Link>
              {", "}
              <Link href="mailto:mmaithani@adobe.com">mmaithani@adobe.com</Link>
              {", "}
              <Link href="mailto:cnikam@adobe.com">cnikam@adobe.com</Link>
            </Text>
          </div>
        </>
      )}

      {figmaLicense === "restricted" && (
        <>
          <S3Image
            filePath="assets/Figma-collab-license-request.jpg"
            styles={style({
              width: "full",
              maxWidth: "[493px]",
              aspectRatio: "493 / 191",
              borderTopRadius: "default",
            })}
          />
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 24,
              textAlign: "center",
            })}
          >
            <Text styles={style({ font: "body-lg" })}>
              The Figma Collab license gives you full access to Figma Slides and
              custom plugins (like BeStory plugin). You’ll need it to fill your
              One Adobe deck with the {video.data?.customer?.name} assets.
            </Text>
            <LinkButton
              variant={variant}
              size="XL"
              href="https://adobe.service-now.com/esc?id=sc_cat_item&sys_id=4bb74fb2c3e34f507412d6df0501319c"
              target="_blank"
            >
              <OpenInIcon />
              <Text>Request Figma Collab license</Text>
            </LinkButton>
            <Text styles={style({ font: "body-sm" })}>
              License cost: $60/y, requires CC manager&apos;s approval
            </Text>
            <Text styles={style({ font: "detail" })}>
              Note: In case of issues with Figma Collab license activation,
              please reach out to the Figma license management team:
              <br />
              <Link href="mailto:cesarc@adobe.com">cesarc@adobe.com</Link>
              {", "}
              <Link href="mailto:mmaithani@adobe.com">mmaithani@adobe.com</Link>
              {", "}
              <Link href="mailto:cnikam@adobe.com">cnikam@adobe.com</Link>
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

FigmaDeckDuplicate.propTypes = {
  assets: PropTypes.array.isRequired,
  video: PropTypes.object.isRequired,
  figmaTemplatePath: PropTypes.string.isRequired,
  variant: PropTypes.string,
};

export const FigmaHandoffStep = ({
  onContinue,
  onBack,
  continueLabel,
  preview = "partnership-value",
  contentLabel = "script",
  canEditCampaign = true,
}) => {
  const { template, video, downloadAllAssets } = useContext(VideoWizardContext);
  const { isLoading: isAssetsLoading } = useVideoAssets(video.data?.id);
  const updateVideo = useUpdateVideo(video.data?.id);
  const { figmaTemplatePath } = template.data ?? {};
  const figmaUrl = video.data?.figmaUrls?.[0]?.url;
  const hasFigmaLink = Boolean(figmaUrl);

  const assets = [
    video.data?.assets?.find((asset) => asset.name === "App-icon"),
    video.data?.assets?.find((asset) => asset.name === "KV-primary"),
    video.data?.assets?.find((asset) => asset.name === "KV-alternative"),
    video.data?.assets?.find((asset) => asset.name === "Ideation-primary"),
  ];

  const brandColor = video.data?.customer?.["Brand-color"];
  const secondaryColor = video.data?.customer?.["Secondary-color"];

  const handleVideoShare = async (emails) => {
    try {
      if (!video.data?.id) throw new Error("Project not found");

      const current = video.data.sharedWith ?? [];
      const currentLower = new Set(current.map((item) => item.toLowerCase()));
      const added = emails.filter((item) => !currentLower.has(item));

      if (added.length === 0) {
        store
          .getState()
          .addToast("Project is already shared with these users", {
            variant: "info",
            timeout: 3000,
          });
        return;
      }

      await updateVideo.mutateAsync({
        id: video.data.id,
        sharedWith: [...current, ...added],
      });

      store.getState().addToast(`Project shared with ${added.join(", ")}`, {
        variant: "positive",
        timeout: 3000,
      });
    } catch (error) {
      store.getState().addToast(error.message || "Failed to share project", {
        variant: "negative",
        timeout: 3000,
      });
      throw error;
    }
  };

  return (
    <>
      <Step
        styles={style({
          gridArea: "content",
          display: "grid",
          gridTemplateAreas: {
            default: ["header", "sidebar", "content", "footer"],
            lg: {
              default: ["header .", "content sidebar", "footer ."],
              isCompleted: ["header", "sidebar", "content", "footer"],
            },
            xl: [". header .", ". content sidebar", ". footer ."],
          },
          gridTemplateColumns: {
            default: "1fr",
            lg: { default: "2fr 1fr", isCompleted: "1fr" },
            xl: "1fr 2fr 1fr",
          },
          gap: 32,
        })({ isCompleted: hasFigmaLink })}
      >
        <Step.Header
          title={
            hasFigmaLink ? (
              `Your ${video.data?.customer?.name} presentation is ready!`
            ) : (
              <>
                <Text>Let’s apply your {video.data?.customer?.name}</Text>
                <br />
                <Text>assets to the One Adobe deck</Text>
              </>
            )
          }
          subtitle={
            hasFigmaLink
              ? `Review the deck and present in Figma Slides`
              : `Almost there!`
          }
          styles={style({
            gridArea: "header",
            flexDirection: { default: "column-reverse", isCompleted: "column" },
          })({ isCompleted: hasFigmaLink })}
        ></Step.Header>

        <div
          className={style({
            gridArea: "content",
            display: "flex",
            flexDirection: "column",
            gap: 48,
          })}
        >
          {hasFigmaLink && (
            <>
              <div
                className={style({
                  position: "relative",
                  width: "full",
                  maxWidth: 600,
                  marginX: "auto",
                  aspectRatio: "16/10",
                  filter: "elevated",
                })}
              >
                <S3Image
                  filePath={assets[1]?.filePath}
                  styles={style({
                    position: "absolute",
                    inset: 0,
                    size: "full",
                    objectFit: "cover",
                    borderRadius: "xl",
                    backgroundColor: "transparent-overlay-200",
                  })}
                />
                <S3Image
                  filePath={assets[0]?.filePath}
                  styles={style({
                    aspectRatio: "square",
                    objectFit: "contain",
                    width: "[15%]",
                    // height: "auto",
                    position: "absolute",
                    insetStart: -24,
                    top: -24,
                    borderRadius: "default",
                    backgroundColor: "transparent-overlay-200",
                  })}
                />
                <div
                  className={style({
                    width: "[8%]",
                    paddingBottom: "[8%]",
                    position: "absolute",
                    insetEnd: "[-2%]",
                    bottom: "[4%]",
                    borderRadius: "default",
                    backgroundColor: "transparent-overlay-200",
                  })}
                  style={!!brandColor ? { backgroundColor: brandColor } : {}}
                />
                <div
                  className={style({
                    width: "[8%]",
                    paddingBottom: "[8%]",
                    position: "absolute",
                    insetEnd: "[-6%]",
                    bottom: "[-2%]",
                    borderRadius: "default",
                    backgroundColor: "transparent-overlay-200",
                  })}
                  style={
                    !!secondaryColor ? { backgroundColor: secondaryColor } : {}
                  }
                />
              </div>

              <div
                className={style({
                  display: "flex",
                  flexDirection: {
                    default: "column",
                    md: "row",
                  },
                  alignItems: "start",
                  gap: 12,
                  alignSelf: "center",
                })}
              >
                <div
                  className={style({
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 12,
                  })}
                >
                  <LinkButton
                    variant="accent"
                    size="XL"
                    href={figmaUrl}
                    target="_blank"
                    isDisabled={!figmaUrl}
                  >
                    <OpenInIcon />
                    <Text>Open in Figma Slides</Text>
                  </LinkButton>
                  <Text styles={style({ font: "detail-sm" })}>
                    Required: Collab or Full license
                  </Text>
                </div>
                <Button
                  variant="primary"
                  size="XL"
                  onPress={() => downloadAllAssets.mutate()}
                  isPending={downloadAllAssets.isPending}
                  isDisabled={isAssetsLoading}
                >
                  <DownloadIcon />
                  <Text>Download all assets</Text>
                </Button>
              </div>
            </>
          )}

          {!hasFigmaLink && (
            <FigmaDeckDuplicate
              video={video}
              assets={assets}
              figmaTemplatePath={figmaTemplatePath}
            />
          )}

          {hasFigmaLink && (
            <Disclosure density="spacious" size="L" isQuiet>
              <DisclosureTitle
                UNSAFE_style={{
                  flexBasis: "300px",
                  flexGrow: 0,
                  margin: "0 auto",
                }}
              >
                <Text styles={style({ marginX: "auto" })}>
                  Want to create a new deck?
                </Text>
              </DisclosureTitle>
              <DisclosurePanel>
                <FigmaDeckDuplicate
                  video={video}
                  assets={assets}
                  figmaTemplatePath={figmaTemplatePath}
                  variant="primary"
                />
              </DisclosurePanel>
            </Disclosure>
          )}
        </div>

        {!hasFigmaLink && (
          <ButtonGroup
            size="L"
            styles={style({
              gridArea: "sidebar",
              alignSelf: { lg: "start" },
              justifySelf: { default: "center", lg: "start" },
            })}
          >
            <DialogTrigger>
              <Button
                variant="primary"
                isPending={updateVideo.isPending}
                isDisabled={!video.data?.id}
              >
                <ShareIcon />
                <Text>Share project</Text>
              </Button>
              <ShareVideoDialog
                videoId={video.data?.id}
                onAccept={handleVideoShare}
              />
            </DialogTrigger>
            <Button
              variant="primary"
              onPress={() => downloadAllAssets.mutate()}
              isPending={downloadAllAssets.isPending}
              isDisabled={isAssetsLoading}
            >
              <DownloadIcon />
              <Text>Download all assets</Text>
            </Button>
          </ButtonGroup>
        )}

        <div
          className={style({
            gridArea: "footer",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            justifySelf: "center",
            gap: 48,
            marginTop: {
              // isCompleted: 80,
            },
          })}
        >
          {video.data?.id && (
            <UserFeedback
              variant="inline"
              title="How did your project turn out?"
              video={video.data}
              currentStep="figma-handoff"
              method="inline"
              styles={style({ width: "full", maxWidth: 400 })}
            />
          )}

          <IllustratedMessage size={hasFigmaLink ? "M" : "M"}>
            <Heading>Do you need help with your project?</Heading>
            <ButtonGroup
              size="L"
              styles={style({
                gridColumnStart: 1,
                gridColumnEnd: 4,
              })}
            >
              <LinkButton
                variant="secondary"
                href="https://adobe.ly/ExperienceDesign-Request"
                target="_blank"
              >
                <Text>Request design assist</Text>
              </LinkButton>
            </ButtonGroup>
          </IllustratedMessage>
        </div>
      </Step>

      <Step.Actions
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={continueLabel}
        isContinueDisabled={false}
      />
    </>
  );
};
